import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { accountApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTokens } from '../contexts/TokenContext';
import {
  ArrowLeft, User, CreditCard, Trash2, Loader2,
  LogOut, AlertTriangle, Coins
} from 'lucide-react';

export default function Settings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { balance } = useTokens();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [cancelResult, setCancelResult] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await accountApi.getSettings();
      setSettings(data);
    } catch (e) {
      console.error('Failed to load settings:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleDeleteAccount = async () => {
    if (deleteTyped !== 'DELETE') return;
    setDeleting(true);
    try {
      await accountApi.deleteAccount();
      await logout();
      navigate('/');
    } catch (e) {
      console.error('Failed to delete account:', e);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const { data } = await accountApi.cancelSubscription();
      setCancelResult(data);
      setShowCancelConfirm(false);
      loadSettings();
    } catch (e) {
      console.error('Failed to cancel subscription:', e);
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F1A' }}>
        <Loader2 className="h-5 w-5 animate-spin text-[#D6A85C]/50" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[#F5EDE4]" style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(180deg, #0B0F1A 0%, #0E1320 100%)' }}>
      <header className="border-b border-[#F5EDE4]/[0.06] px-6 md:px-12 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#F5EDE4]/40 hover:text-[#D6A85C] transition-colors duration-300" data-testid="settings-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <span className="text-base font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Settings</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/pricing" className="text-xs text-[#F5EDE4]/40 hover:text-[#D6A85C] transition-colors duration-300" data-testid="nav-pricing">Pricing</Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs text-[#F5EDE4]/40 hover:text-[#D6A85C] transition-colors duration-300"
              data-testid="settings-logout-btn"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-10 space-y-8">
        {/* Account */}
        <section className="border border-[#F5EDE4]/[0.06] p-6" data-testid="account-info-section">
          <div className="flex items-center gap-3 mb-5">
            <User className="h-4 w-4 text-[#F5EDE4]/40" />
            <h2 className="text-sm font-medium uppercase tracking-[0.15em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Account</h2>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-[#F5EDE4]/40">Email</span>
              <span>{settings?.email || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#F5EDE4]/40">Tokens</span>
              <span className="flex items-center gap-1.5">
                <Coins className="h-3.5 w-3.5 text-[#D6A85C]/50" />
                {balance ?? settings?.token_balance ?? 0}
              </span>
            </div>
          </div>
        </section>

        {/* Subscription */}
        <section className="border border-[#F5EDE4]/[0.06] p-6" data-testid="subscription-section">
          <div className="flex items-center gap-3 mb-5">
            <CreditCard className="h-4 w-4 text-[#F5EDE4]/40" />
            <h2 className="text-sm font-medium uppercase tracking-[0.15em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Subscription</h2>
          </div>

          {cancelResult && (
            <div className="bg-green-500/5 border border-green-500/20 p-4 mb-4" data-testid="cancel-success-msg">
              <p className="text-sm text-green-400">{cancelResult.message}</p>
            </div>
          )}

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-[#F5EDE4]/40">Status</span>
              <span className={settings?.subscription_status === 'cancelled' ? 'text-amber-400' : 'text-green-400'}>
                {settings?.subscription_status || 'Active'}
              </span>
            </div>
          </div>

          {settings?.subscription_status !== 'cancelled' && (
            <div className="mt-6">
              {!showCancelConfirm ? (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="px-4 py-2 text-xs border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors duration-300"
                  data-testid="cancel-subscription-btn"
                >
                  Cancel Subscription
                </button>
              ) : (
                <div className="border border-amber-500/20 bg-amber-500/5 p-4 space-y-4" data-testid="cancel-confirm-panel">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Cancel subscription?</p>
                      <p className="text-xs text-[#F5EDE4]/40 mt-1">Access continues until the end of your billing period.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowCancelConfirm(false)} className="px-4 py-2 text-xs border border-[#F5EDE4]/[0.06] text-[#F5EDE4]/40 hover:text-[#F5EDE4] transition-colors" data-testid="cancel-sub-back-btn">Keep</button>
                    <button onClick={handleCancelSubscription} disabled={cancelling} className="px-4 py-2 text-xs bg-amber-500 text-black hover:bg-amber-600 disabled:opacity-30 transition-colors" data-testid="cancel-sub-confirm-btn">
                      {cancelling && <Loader2 className="h-3 w-3 animate-spin inline mr-1" />}Confirm
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Delete Account */}
        <section className="border border-red-500/20 p-6" data-testid="delete-account-section">
          <div className="flex items-center gap-3 mb-5">
            <Trash2 className="h-4 w-4 text-red-400" />
            <h2 className="text-sm font-medium uppercase tracking-[0.15em] text-red-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Delete Account</h2>
          </div>
          {!showDeleteConfirm ? (
            <>
              <p className="text-sm text-[#F5EDE4]/40 mb-4">Permanently delete your account and all data. This cannot be undone.</p>
              <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors" data-testid="delete-account-btn">Delete My Account</button>
            </>
          ) : (
            <div className="space-y-4" data-testid="delete-confirm-panel">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-400">This is permanent.</p>
                  <p className="text-xs text-[#F5EDE4]/40 mt-1">All projects, scripts, and history will be permanently deleted.</p>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#F5EDE4]/40">Type <span className="text-red-400 font-mono font-bold">DELETE</span> to confirm:</label>
                <input
                  type="text" value={deleteTyped} onChange={e => setDeleteTyped(e.target.value)}
                  className="w-full mt-2 bg-transparent border border-red-500/30 px-3 py-2 text-sm text-[#F5EDE4] focus:outline-none focus:border-red-500/60"
                  placeholder="DELETE" data-testid="delete-confirm-input"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowDeleteConfirm(false); setDeleteTyped(''); }} className="px-4 py-2 text-xs border border-[#F5EDE4]/[0.06] text-[#F5EDE4]/40 hover:text-[#F5EDE4] transition-colors" data-testid="delete-cancel-btn">Cancel</button>
                <button disabled={deleteTyped !== 'DELETE' || deleting} onClick={handleDeleteAccount} className="px-4 py-2 text-xs bg-red-500 text-white hover:bg-red-600 disabled:opacity-30 transition-colors" data-testid="delete-confirm-btn">
                  {deleting && <Loader2 className="h-3 w-3 animate-spin inline mr-1" />}Permanently Delete
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
